import { DateOnlyInput } from "../components/DateOnlyInput";
import { alphaMutationRejected } from "../alpha-mutation";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, authenticatedFetch } from "../api";
import { ErrorState, Loading } from "../ui/State";
import { useSearchParams } from "react-router";

const kinds = [
  "Landlord licence",
  "EICR",
  "EPC",
  "Gas safety certificate",
  "Floor plan",
  "Other property document",
];
export function HouseholdAlpha({ premises }: { premises: string }) {
  const root = "/api/portal/leaseholder/" + premises + "/household";
  const [model, setModel] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState("");
  const pending = useRef<{ url: string; body: any } | null>(null);
  const load = () => api(root).then(setModel);
  useEffect(() => {
    let active = true;
    api(root)
      .then((r) => {
        if (active) setModel(r);
      })
      .catch((e) => {
        if (active) setError(e);
      });
    return () => {
      active = false;
    };
  }, [root]);
  async function save(url: string, body: any) {
    pending.current ??= {
      url,
      body: { ...body, idempotencyKey: crypto.randomUUID() },
    };
    setBusy(true);
    try {
      await api(pending.current.url, {
        method: "POST",
        body: JSON.stringify(pending.current.body),
      });
      pending.current = null;
      setNotice("Saved for your manager to review.");
      setError(undefined);
      await load();
    } catch (e) {
      if (alphaMutationRejected(e)) pending.current = null;
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  function contacts(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void save(root + "/contacts", {
      givenNames: f.get("givenNames"),
      familyName: f.get("familyName"),
      role: f.get("role"),
      startDate: f.get("startDate"),
      ...(f.get("endDate") ? { endDate: f.get("endDate") } : {}),
      ...(f.get("email") ? { email: f.get("email") } : {}),
      ...(f.get("telephone") ? { telephone: f.get("telephone") } : {}),
    });
  }
  async function upload(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget),
      file = f.get("file") as File;
    if (!file?.size || file.size > 10 * 1024 * 1024) {
      setError(Error("Choose a PDF, JPEG or PNG up to 10 MB."));
      return;
    }
    setBusy(true);
    try {
      const content = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1]!);
        r.onerror = () => reject(Error("Unable to read file."));
        r.readAsDataURL(file);
      });
      await save(root + "/documents", {
        kind: f.get("kind"),
        fileName: file.name,
        contentBase64: content,
        ...(f.get("expiryDate") ? { expiryDate: f.get("expiryDate") } : {}),
      });
    } catch (e) {
      if (alphaMutationRejected(e)) pending.current = null;
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  async function download(d: any) {
    try {
      const r = await authenticatedFetch(root + "/documents/" + d.version_id);
      if (!r.ok)
        throw Error("Document unavailable or awaiting a security check.");
      const u = URL.createObjectURL(await r.blob()),
        a = document.createElement("a");
      a.href = u;
      a.download = d.title;
      a.click();
      setTimeout(() => URL.revokeObjectURL(u), 1000);
    } catch (e) {
      setError(e);
    }
  }
  return (
    <section className="panel">
      <h2>People and property documents</h2>
      {Boolean(error) && <ErrorState error={error} />}
      <p role="status">{notice}</p>
      {pending.current && !busy && (
        <button onClick={() => void save("", {})}>
          Retry the same submission
        </button>
      )}
      {!model ? (
        <Loading />
      ) : (
        <>
          <h3>Contacts you have submitted</h3>
          {model.contacts.length === 0 && <p>No contacts submitted yet.</p>}
          {model.contacts.map((c: any) => (
            <p key={c.id}>
              {c.proposal.givenNames} {c.proposal.familyName} ·{" "}
              {c.proposal.role} · {c.status}
              {c.review_note && <> — {c.review_note}</>}
            </p>
          ))}
          <form className="form" onSubmit={contacts}>
            <fieldset disabled={busy || !!pending.current}>
              <legend>Add an occupier or contact</legend>
              <label>
                First names
                <input name="givenNames" required maxLength={100} />
              </label>
              <label>
                Last name
                <input name="familyName" required maxLength={100} />
              </label>
              <label>
                Relationship
                <select name="role">
                  <option>Occupier</option>
                  <option>Letting agent contact</option>
                  <option>Emergency contact</option>
                </select>
              </label>
              <label>
                Email
                <input name="email" type="email" />
              </label>
              <label>
                Telephone
                <input name="telephone" type="tel" />
              </label>
              <label>
                From
                <DateOnlyInput name="startDate" required />
              </label>
              <label>
                Until, if known
                <DateOnlyInput name="endDate" />
              </label>
              <p>
                Your manager reviews these details. Adding a person does not
                give them access to your account.
              </p>
              <button>Submit contact</button>
            </fieldset>
          </form>
          <h3>Your uploaded documents</h3>
          {model.documents.length === 0 && (
            <p>No property documents uploaded yet.</p>
          )}
          {model.documents.map((d: any) => (
            <p key={d.id}>
              <button onClick={() => void download(d)}>{d.title}</button> ·{" "}
              {d.verification_status}
              {d.expiry_date && <> · Expires {d.expiry_date}</>}
            </p>
          ))}
          <form className="form" onSubmit={(e) => void upload(e)}>
            <fieldset disabled={busy || !!pending.current}>
              <legend>Upload a property document</legend>
              <label>
                Document
                <select name="kind" aria-label="Document">
                  {kinds.map((k) => (
                    <option key={k}>{k}</option>
                  ))}
                </select>
              </label>
              <label>
                File — PDF, JPEG or PNG, up to 10 MB
                <input
                  name="file"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  required
                />
              </label>
              <label>
                Expiry date, if shown
                <DateOnlyInput name="expiryDate" />
              </label>
              <p>
                Supply documents relevant to your property. Your manager reviews
                each one; receiving a file is not a compliance check.
              </p>
              <button>Upload document</button>
            </fieldset>
          </form>
        </>
      )}
    </section>
  );
}
export function HouseholdReviewAlpha() {
  const [query] = useSearchParams(),
    building = query.get("building") ?? "";
  const [model, setModel] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false);
  const pending = useRef<{ url: string; body: any } | null>(null);
  const load = () =>
    api("/api/alpha/households?building=" + building).then(setModel);
  useEffect(() => {
    setModel(undefined);
    if (building) void load().catch(setError);
  }, [building]);
  async function review(id: string, e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    pending.current ??= {
      url: "/api/alpha/households/" + id + "/review",
      body: {
        decision: f.get("decision"),
        note: f.get("note"),
        ...(f.get("person") ? { existingPersonId: f.get("person") } : {}),
        idempotencyKey: crypto.randomUUID(),
      },
    };
    await send();
  }
  async function send() {
    if (!pending.current) return;
    setBusy(true);
    try {
      await api(pending.current.url, {
        method: "POST",
        body: JSON.stringify(pending.current.body),
      });
      pending.current = null;
      setError(undefined);
      await load();
    } catch (e) {
      if (alphaMutationRejected(e)) pending.current = null;
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <h1>Leaseholder contact submissions</h1>
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {!building ? (
        <p>Choose a building.</p>
      ) : !model ? (
        <Loading />
      ) : (
        <>
          {pending.current && !busy && (
            <button onClick={() => void send()}>Retry the same review</button>
          )}
          {model.proposals.length === 0 && <p>No contact submissions.</p>}
          {model.proposals.map((p: any) => (
            <section className="panel" key={p.id}>
              <h2>
                {p.premises_name} · {p.proposal.givenNames}{" "}
                {p.proposal.familyName}
              </h2>
              <p>
                {p.proposal.role} · {p.status}
              </p>
              <p>
                {p.proposal.email} {p.proposal.telephone} · From{" "}
                {p.proposal.startDate}
                {p.proposal.endDate && <> until {p.proposal.endDate}</>}
              </p>
              <p>{p.review_note}</p>
              {["Awaiting review", "Accepted"].includes(p.status) && (
                <form className="form" onSubmit={(e) => void review(p.id, e)}>
                  <fieldset disabled={busy || !!pending.current}>
                    <legend>Review contact</legend>
                    <label>
                      Decision
                      <select name="decision">
                        {p.status === "Accepted" ? (
                          <option>End</option>
                        ) : (
                          <>
                            <option>Accept</option>
                            <option>Reject</option>
                          </>
                        )}
                      </select>
                    </label>
                    {p.status === "Awaiting review" && (
                      <label>
                        Existing contact reference, if already registered
                        <input
                          name="person"
                          placeholder="Leave blank to create a new contact"
                        />
                      </label>
                    )}
                    <label>
                      Review note
                      <textarea
                        name="note"
                        required
                        minLength={3}
                        maxLength={2000}
                      />
                    </label>
                    <button>Save review</button>
                  </fieldset>
                </form>
              )}
            </section>
          ))}
        </>
      )}
    </>
  );
}
